import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, HomeIcon } from "@heroicons/react/24/outline";
import { useCart } from "../../contexts";
import { StyledButton } from "../../components/Typography";
import { formatPrice } from "../../helpers/textTransformation";
import { createAuctionPlaceholderImage } from "../../lib/auctionPresentation";

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SuccessHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(260px, 360px);
  gap: 22px;
  padding: 24px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(246, 163, 28, 0.18), transparent 28%),
    linear-gradient(135deg, #0c1129 0%, #11193d 100%);
  color: ${({ theme }) => theme.colors.white};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 767px) {
    padding: 20px 18px;
    border-radius: 24px;
  }
`;

const SuccessCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SuccessBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.blue500};
`;

const SuccessTitle = styled.h2`
  margin: 0;
  font-family: "Sofia Pro", sans-serif;
  font-size: 34px;
  line-height: 1.08;
  font-weight: 700;

  @media (max-width: 767px) {
    font-size: 28px;
  }
`;

const SuccessMessage = styled.p`
  margin: 0;
  max-width: 640px;
  font-family: "Sofia Pro", sans-serif;
  font-size: 16px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.82);
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 6px;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.div`
  padding: 16px 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const DetailLabel = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 6px;
`;

const DetailValue = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 18px;
  line-height: 1.4;
  font-weight: 700;
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const ProductImage = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
`;

const ProductTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
`;

const ProductPrice = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 26px;
  line-height: 1.1;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 22px 24px;
  border-radius: 24px;
  background: ${({ theme }) => theme.colors.grey100};

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    padding: 18px;
  }
`;

const ActionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ActionTitle = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.blue900};
`;

const ActionDescription = styled.div`
  font-family: "Sofia Pro", sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.blue500};
`;

const HomeButton = styled(StyledButton)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;

  @media (max-width: 767px) {
    width: 100%;
    justify-content: center;
  }
`;

const Fourth = () => {
  const navigate = useNavigate();
  const { cartProduct, buyerInfo, clearCart } = useCart();

  const handleGoHome = () => {
    clearCart();
    navigate("/");
  };

  if (!cartProduct) {
    return null;
  }

  const imageFallback = createAuctionPlaceholderImage(cartProduct.title);

  return (
    <SummaryContainer>
      <SuccessHero>
        <SuccessCopy>
          <SuccessBadge>
            <CheckCircleIcon style={{ width: 34, height: 34 }} />
          </SuccessBadge>
          <SuccessTitle>Objednávka dokončena</SuccessTitle>
          <SuccessMessage>
            Platba byla potvrzena a objednávku jsme uzavřeli. Další informace
            pošleme na kontaktní e-mail, který jste zadali v předchozím kroku.
          </SuccessMessage>

          <DetailGrid>
            <DetailCard>
              <DetailLabel>Potvrzení</DetailLabel>
              <DetailValue>{buyerInfo?.email || "Na vyplněný e-mail"}</DetailValue>
            </DetailCard>
            <DetailCard>
              <DetailLabel>Celková cena</DetailLabel>
              <DetailValue>{formatPrice(cartProduct.price)}</DetailValue>
            </DetailCard>
          </DetailGrid>
        </SuccessCopy>

        <ProductCard>
          <ProductImage
            src={cartProduct.photoUrls?.[0] || imageFallback}
            alt={cartProduct.title}
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = imageFallback;
            }}
          />
          <div>
            <DetailLabel>Dokončená objednávka</DetailLabel>
            <ProductTitle>{cartProduct.title}</ProductTitle>
          </div>
          <ProductPrice>{formatPrice(cartProduct.price)}</ProductPrice>
        </ProductCard>
      </SuccessHero>

      <ActionBar>
        <ActionText>
          <ActionTitle>Vrátit se zpět na aukce</ActionTitle>
          <ActionDescription>
            Košík se při návratu vyčistí, takže můžete rovnou pokračovat na další položky.
          </ActionDescription>
        </ActionText>

        <HomeButton onClick={handleGoHome}>
          <HomeIcon style={{ width: 18, height: 18 }} />
          Zpět na hlavní stránku
        </HomeButton>
      </ActionBar>
    </SummaryContainer>
  );
};

export default Fourth;
